/* Zaroori Jini OMS — Contact Sales page.
 * Enterprise lead form (client-side only for now). Submission opens an email
 * client via mailto: with pre-filled context. A backend endpoint can be added
 * later without changing this page's UX.
 */
import { useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, Building2, User, Users, ShoppingCart, MessageSquare, MapPin, CheckCircle2 } from "lucide-react";
import {
  RED, INK, MUTED, CREAM, BORDER,
  Chip, Section, PrimaryButton, FieldLabel, FieldInput, MarketingShell,
} from "@/components/marketing/atoms";

const VOLUME = ["<1,000 orders/mo", "1,000-5,000", "5,000-20,000", "20,000-100,000", "100,000+"];

export default function ContactSales() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "",
    role: "", teamSize: "", volume: "1,000-5,000",
    marketplaces: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.name || !form.company) {
      return toast.error("Please fill name, work email, and company.");
    }
    setSubmitting(true);
    try {
      const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
      const r = await fetch(`${backend}/api/leads/contact-sales`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!r.ok) throw new Error(await r.text());
      setDone(true);
      toast.success("Inquiry received! Check your inbox — our team will reply within 4 business hours.");
    } catch (err) {
      toast.error("Something went wrong — please email us directly at hello@zarooriinfotech.in");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MarketingShell testid="contact-sales-page">
      <section
        style={{
          background: `linear-gradient(180deg, ${CREAM} 0%, #fff 100%)`,
          padding: "64px 24px 40px", borderBottom: `1px solid ${BORDER}`,
        }}
      >
        <div style={{ maxWidth: 900, margin: "0 auto", textAlign: "center" }}>
          <Chip>Contact Sales</Chip>
          <h1
            style={{
              fontSize: "clamp(36px, 5vw, 56px)", fontWeight: 800, color: INK,
              marginTop: 18, letterSpacing: "-0.03em", lineHeight: 1.05,
            }}
          >
            Talk to a Commerce Ops Expert
          </h1>
          <p
            style={{
              color: MUTED, fontSize: 16, lineHeight: 1.65,
              maxWidth: 640, margin: "20px auto 0",
            }}
          >
            Get a personalized demo, migration plan, and custom quote — usually within one working day.
          </p>
        </div>
      </section>

      <Section style={{ background: "#fff" }} id="form">
        <div
          className="contact-grid"
          style={{ display: "grid", gridTemplateColumns: "1.2fr 0.9fr", gap: 40, alignItems: "start" }}
        >
          {/* Form */}
          <form
            onSubmit={submit}
            data-testid="contact-sales-form"
            style={{
              background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 16,
              padding: 28, boxShadow: "0 20px 50px -30px rgba(15,23,42,0.15)",
            }}
          >
            <div className="contact-form-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <div>
                <FieldLabel>Full Name *</FieldLabel>
                <FieldInput icon={<User size={14} color={MUTED} />} value={form.name} onChange={upd("name")} placeholder="Your name" testid="cs-name" />
              </div>
              <div>
                <FieldLabel>Work Email *</FieldLabel>
                <FieldInput icon={<Mail size={14} color={MUTED} />} type="email" value={form.email} onChange={upd("email")} placeholder="you@brand.com" testid="cs-email" />
              </div>
              <div>
                <FieldLabel>Phone</FieldLabel>
                <FieldInput icon={<Phone size={14} color={MUTED} />} value={form.phone} onChange={upd("phone")} placeholder="+91 98765 43210" testid="cs-phone" />
              </div>
              <div>
                <FieldLabel>Company *</FieldLabel>
                <FieldInput icon={<Building2 size={14} color={MUTED} />} value={form.company} onChange={upd("company")} placeholder="Company / Brand" testid="cs-company" />
              </div>
              <div>
                <FieldLabel>Your Role</FieldLabel>
                <FieldInput icon={<User size={14} color={MUTED} />} value={form.role} onChange={upd("role")} placeholder="e.g. Head of Ops" testid="cs-role" />
              </div>
              <div>
                <FieldLabel>Team Size</FieldLabel>
                <FieldInput icon={<Users size={14} color={MUTED} />} value={form.teamSize} onChange={upd("teamSize")} placeholder="e.g. 12" testid="cs-teamsize" />
              </div>
            </div>

            <div style={{ marginTop: 14 }}>
              <FieldLabel>Monthly Order Volume</FieldLabel>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }} data-testid="cs-volume-picker">
                {VOLUME.map((v) => {
                  const active = form.volume === v;
                  return (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, volume: v }))}
                      style={{
                        padding: "8px 14px",
                        border: `1px solid ${active ? RED : BORDER}`,
                        background: active ? "#FFF5F5" : "#fff",
                        color: active ? RED : INK,
                        borderRadius: 999, fontSize: 12, fontWeight: 700,
                        cursor: "pointer",
                      }}
                    >
                      {v}
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={{ marginTop: 14 }}>
              <FieldLabel>Marketplaces you sell on</FieldLabel>
              <FieldInput
                icon={<ShoppingCart size={14} color={MUTED} />}
                value={form.marketplaces} onChange={upd("marketplaces")}
                placeholder="e.g. Amazon, Flipkart, Myntra, Shopify"
                testid="cs-marketplaces"
              />
            </div>

            <div style={{ marginTop: 14 }}>
              <FieldLabel>How can we help?</FieldLabel>
              <FieldInput
                as="textarea"
                icon={<MessageSquare size={14} color={MUTED} />}
                value={form.message} onChange={upd("message")}
                placeholder="Tell us about your current setup, tools, integrations, timelines…"
                rows={5} testid="cs-message"
              />
            </div>

            <div style={{ marginTop: 22 }}>
              <PrimaryButton type="submit" disabled={submitting} testid="cs-submit">
                {submitting ? "Preparing…" : "Send Inquiry"}
              </PrimaryButton>
            </div>

            {done && (
              <div
                data-testid="cs-success"
                style={{
                  marginTop: 20, padding: "14px 16px", background: "#ECFDF5",
                  border: "1px solid #A7F3D0", color: "#065F46", borderRadius: 8,
                  display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13,
                }}
              >
                <CheckCircle2 size={18} />
                <div>
                  Your draft has been prepared in your email client. If it didn&apos;t open,
                  email us directly at <b>sales@zaroorijini.com</b>.
                </div>
              </div>
            )}
          </form>

          {/* Sidebar */}
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <SideCard
              title="Direct contacts"
              rows={[
                { icon: <Mail size={14} />, label: "sales@zaroorijini.com", href: "mailto:sales@zaroorijini.com" },
                { icon: <Phone size={14} />, label: "+91 98765 43210", href: "tel:+919876543210" },
                { icon: <MapPin size={14} />, label: "Zaroori Retail HQ, Gurugram, India" },
              ]}
            />
            <SideCard
              title="What happens next"
              list={[
                "We reply within 4 business hours",
                "45-min discovery + demo call",
                "Personalized migration plan & quote",
                "Sandbox tenant with your marketplaces",
              ]}
            />
            <div
              style={{
                background: "#0F172A", color: "#fff", borderRadius: 14,
                padding: 22,
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 700, color: "#F87171", letterSpacing: "0.14em", textTransform: "uppercase" }}>
                Enterprise / Agency?
              </div>
              <div style={{ fontSize: 17, fontWeight: 700, marginTop: 8, lineHeight: 1.4 }}>
                We offer white-label, SSO, and custom SLAs for agencies and 100K+ order brands.
              </div>
            </div>
          </div>
        </div>
        <style>{`
          @media (max-width: 960px) {
            .contact-grid { grid-template-columns: 1fr !important; }
          }
          @media (max-width: 640px) {
            .contact-form-grid { grid-template-columns: 1fr !important; }
          }
        `}</style>
      </Section>
    </MarketingShell>
  );
}

function SideCard({ title, rows, list }) {
  return (
    <div
      style={{
        background: CREAM, border: `1px solid ${BORDER}`, borderRadius: 14,
        padding: 22,
      }}
    >
      <div style={{ fontSize: 12, color: RED, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 12 }}>
        {title}
      </div>
      {rows &&
        rows.map((r) => (
          <div key={r.label} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0" }}>
            <span style={{ color: RED }}>{r.icon}</span>
            {r.href ? (
              <a
                href={r.href}
                style={{ color: INK, textDecoration: "none", fontSize: 13, fontWeight: 600 }}
              >
                {r.label}
              </a>
            ) : (
              <span style={{ color: INK, fontSize: 13, fontWeight: 600 }}>{r.label}</span>
            )}
          </div>
        ))}
      {list &&
        list.map((it, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0" }}>
            <span style={{
              width: 20, height: 20, borderRadius: "50%", background: "#fff",
              display: "grid", placeItems: "center", fontSize: 11,
              fontWeight: 800, color: RED, border: `1px solid ${BORDER}`,
            }}>
              {i + 1}
            </span>
            <span style={{ color: INK, fontSize: 13, fontWeight: 600 }}>{it}</span>
          </div>
        ))}
    </div>
  );
}
